import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Badge, Col, Input, Pagination, PaginationItem, PaginationLink, Row, Spinner, Table,
} from "reactstrap";
import ExpenseManagerService from "../../../Services/expenseManager/ExpenseManagerService";
import { formatMoney } from "../../../utils/formatMoney";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import {
  accountingStatusMeta, formatShortDate, paymentStatusMeta, statusMeta,
} from "./expenseManagerUtils";
import ExpenseDetailModal from "./ExpenseDetailModal";

const PER_PAGE = 25;

/** Backend treats these index filters as arrays (a single value must still be sent as a list). */
const ARRAY_FILTERS = ["company_id", "category_id", "status", "currency"];

export default function ExpensesTab({ meta, onChanged }) {
  const [filters, setFilters] = useState({});
  const [qInput, setQInput] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((prev) => ({ ...prev, q: qInput }));
      setPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [qInput]);

  const categoriesQuery = useQuery({
    queryKey: ["expenseManagerCategories"],
    queryFn: ExpenseManagerService.getCategories,
    staleTime: 60_000,
  });

  const params = useMemo(() => {
    const clean = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      if (ARRAY_FILTERS.includes(key)) clean[key] = [value];
      else clean[key] = value;
    });
    return { ...clean, page, per_page: PER_PAGE, with_totals: 1, sort: "expense_date", direction: "desc" };
  }, [filters, page]);

  const query = useQuery({
    queryKey: ["expenseManagerExpenses", params],
    queryFn: () => ExpenseManagerService.getExpenses(params),
    placeholderData: (previous) => previous,
  });

  const rows = query.data?.data || [];
  const total = query.data?.meta?.total || 0;
  const lastPage = query.data?.meta?.last_page || 1;

  const update = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  const categoryOptions = useMemo(() => {
    const flat = [];
    const walk = (items, depth) => {
      (items || []).forEach((item) => {
        flat.push({ id: item.id, name: `${"— ".repeat(depth)}${item.name}` });
        walk(item.children, depth + 1);
      });
    };
    walk(categoriesQuery.data?.data, 0);
    return flat;
  }, [categoriesQuery.data]);

  return (
    <div className="em-expenses">
      <Row className="g-2 mb-2">
        <Col lg={3} md={6}>
          <Input
            placeholder="Search reference, title, supplier…"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            aria-label="Search expenses"
          />
        </Col>
        <Col lg={2} md={3}>
          <Input type="select" value={filters.company_id || ""} onChange={(e) => update({ company_id: e.target.value })} aria-label="Company">
            <option value="">All companies</option>
            {meta.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Input>
        </Col>
        <Col lg={2} md={3}>
          <Input type="select" value={filters.status || ""} onChange={(e) => update({ status: e.target.value })} aria-label="Status">
            <option value="">Any status</option>
            {meta.statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Input>
        </Col>
        <Col lg={2} md={3}>
          <Input type="select" value={filters.payment_status || ""} onChange={(e) => update({ payment_status: e.target.value })} aria-label="Payment status">
            <option value="">Any payment</option>
            {meta.payment_status.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Input>
        </Col>
        <Col lg={3} md={3}>
          <Input type="select" value={filters.category_id || ""} onChange={(e) => update({ category_id: e.target.value })} aria-label="Category">
            <option value="">Any category</option>
            {categoryOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Input>
        </Col>
        <Col lg={2} md={3}>
          <Input type="select" value={filters.accounting_status || ""} onChange={(e) => update({ accounting_status: e.target.value })} aria-label="Accounting status">
            <option value="">Any accounting</option>
            {meta.accounting_status.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Input>
        </Col>
        <Col lg={2} md={3}>
          <Input type="date" value={filters.expense_date_from || ""} onChange={(e) => update({ expense_date_from: e.target.value })} aria-label="From date" />
        </Col>
        <Col lg={2} md={3}>
          <Input type="date" value={filters.expense_date_to || ""} onChange={(e) => update({ expense_date_to: e.target.value })} aria-label="To date" />
        </Col>
        <Col lg="auto" className="d-flex align-items-center">
          <span className="text-muted small">{total} expenses</span>
        </Col>
      </Row>

      {query.isError && (
        <div className="alert alert-danger">{getApiErrorMessage(query.error, "Could not load expenses.")}</div>
      )}

      <div className="table-responsive">
        <Table size="sm" hover className="em-table align-middle">
          <thead>
            <tr>
              <th>Reference</th>
              <th>Date</th>
              <th>Title</th>
              <th>Supplier</th>
              <th className="text-end">Net</th>
              <th className="text-end">Tax</th>
              <th className="text-end">Gross</th>
              <th className="text-end">Paid</th>
              <th>Status</th>
              <th>Payment</th>
              <th>Accounting</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr><td colSpan="11" className="text-center py-4"><Spinner size="sm" /></td></tr>
            )}
            {!query.isLoading && rows.length === 0 && (
              <tr><td colSpan="11" className="text-center text-muted py-4">No expenses found.</td></tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} className="em-row" onClick={() => setSelected(row.id)} role="button">
                <td className="text-nowrap">{row.reference}</td>
                <td className="text-nowrap">{formatShortDate(row.expense_date)}</td>
                <td>
                  {row.title}
                  {row.company ? <div className="text-muted small">{row.company.name}</div> : null}
                </td>
                <td>{row.supplier || row.supplier_name || "—"}</td>
                <td className="text-end text-nowrap">{formatMoney(row.net_amount, row.currency_code)}</td>
                <td className="text-end text-nowrap">{formatMoney(row.tax_amount, row.currency_code)}</td>
                <td className="text-end text-nowrap">{formatMoney(row.gross_amount, row.currency_code)}</td>
                <td className="text-end text-nowrap">{formatMoney(row.amount_paid, row.currency_code)}</td>
                <td><Badge color={statusMeta(row.status).color} pill>{statusMeta(row.status).label}</Badge></td>
                <td><Badge color={paymentStatusMeta(row.payment_status).color} pill>{paymentStatusMeta(row.payment_status).label}</Badge></td>
                <td><Badge color={accountingStatusMeta(row.accounting_status).color} pill>{accountingStatusMeta(row.accounting_status).label}</Badge></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {lastPage > 1 && (
        <Pagination size="sm" className="mt-2 justify-content-end">
          <PaginationItem disabled={page <= 1}>
            <PaginationLink previous onClick={() => setPage((p) => Math.max(1, p - 1))} />
          </PaginationItem>
          <PaginationItem disabled>
            <PaginationLink>Page {page} / {lastPage}</PaginationLink>
          </PaginationItem>
          <PaginationItem disabled={page >= lastPage}>
            <PaginationLink next onClick={() => setPage((p) => Math.min(lastPage, p + 1))} />
          </PaginationItem>
        </Pagination>
      )}

      {selected && (
        <ExpenseDetailModal
          isOpen
          expenseId={selected}
          meta={meta}
          toggle={() => setSelected(null)}
          onChanged={() => {
            query.refetch();
            if (onChanged) onChanged();
          }}
        />
      )}
    </div>
  );
}
